import React, { useState, useEffect } from 'react';
import { Users, Plus, Check, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';

const COHORT_TYPES = [
  { value: 'ongoing', label: 'Ongoing', description: 'Permanent team or department' },
  { value: 'time_bound', label: 'Time-Bound', description: 'Program with start/end dates' },
  { value: 'project_based', label: 'Project-Based', description: 'Specific initiative cohort' }
];

/**
 * Reusable Cohort Selector Component
 * 
 * Features:
 * - Select from existing cohorts
 * - Create new cohorts inline
 * - Filter/search cohorts
 * - Shows cohort metadata (member count, type)
 * 
 * Props:
 * - selectedCohort: Currently selected cohort object or null
 * - onCohortChange: Callback when cohort is selected (receives full cohort object)
 * - placeholder: Placeholder text when nothing selected
 * - disabled: Disable the selector
 * - showCreateOption: Show the "Create New" option (default: true)
 * - className: Additional CSS classes
 */
const CohortSelector = ({ 
  selectedCohort, 
  onCohortChange, 
  placeholder = "Select a cohort (optional)",
  disabled = false,
  showCreateOption = true,
  className = "",
  cohorts: externalCohorts = null
}) => {
  const [open, setOpen] = useState(false);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // New cohort form state
  const [newCohort, setNewCohort] = useState({
    name: '',
    type: 'ongoing',
    description: '',
    start_date: '',
    end_date: '',
    demographics: {
      population_size: '',
      total_workforce: '',
      departments: '',
      regions: '',
      tenure_breakdown: '',
      role_levels: ''
    }
  });
  
  // Expanded demographics form state
  const [showDemographics, setShowDemographics] = useState(false);

  // Use external cohorts if provided, otherwise fetch from API
  useEffect(() => {
    if (externalCohorts) {
      setCohorts(externalCohorts);
    } else if (open) {
      fetchCohorts();
    }
  }, [open, externalCohorts]);

  const fetchCohorts = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/segments/cohorts`, {
        params: { include_demo: false, limit: 100 }
      });
      setCohorts(response.data.cohorts || []);
    } catch (error) {
      logger.error('Error fetching cohorts:', error);
      toast.error('Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const createCohort = async () => {
    if (!newCohort.name.trim()) {
      toast.error('Please enter a cohort name');
      return;
    }

    setCreating(true);
    try {
      // Parse demographics data
      const parseDemoField = (value) => {
        if (!value || !value.trim()) return null;
        try {
          // Format: "Sales: 45, Marketing: 30" or JSON
          if (value.trim().startsWith('{')) {
            return JSON.parse(value);
          }
          const pairs = value.split(',').map(p => p.trim());
          const result = {};
          pairs.forEach(pair => {
            const [key, val] = pair.split(':').map(s => s.trim());
            if (key && val) {
              result[key] = parseInt(val) || 0;
            }
          });
          return Object.keys(result).length > 0 ? result : null;
        } catch {
          return null;
        }
      };
      
      const demographics = {
        population_size: parseInt(newCohort.demographics.population_size) || null,
        total_workforce: parseInt(newCohort.demographics.total_workforce) || null,
        departments: parseDemoField(newCohort.demographics.departments),
        regions: parseDemoField(newCohort.demographics.regions),
        tenure_breakdown: parseDemoField(newCohort.demographics.tenure_breakdown),
        role_levels: parseDemoField(newCohort.demographics.role_levels)
      };
      
      // Only include demographics if at least one field is set
      const hasDemographics = Object.values(demographics).some(v => v !== null);
      
      const payload = {
        name: newCohort.name.trim(),
        type: newCohort.type,
        description: newCohort.description.trim() || null,
        start_date: newCohort.start_date || null,
        end_date: newCohort.end_date || null,
        demographics: hasDemographics ? demographics : null
      };

      const response = await api.post(`/segments/cohorts`, payload);
      const createdCohort = response.data;
      
      // Add to local list
      setCohorts(prev => [createdCohort, ...prev]);
      
      // Select the newly created cohort
      onCohortChange(createdCohort);
      
      // Reset form and close dialogs
      setNewCohort({ 
        name: '', 
        type: 'ongoing', 
        description: '', 
        start_date: '', 
        end_date: '',
        demographics: {
          population_size: '',
          total_workforce: '',
          departments: '',
          regions: '',
          tenure_breakdown: '',
          role_levels: ''
        }
      });
      setShowDemographics(false);
      setShowCreateDialog(false);
      setOpen(false);
      
      toast.success(`Cohort "${createdCohort.name}" created successfully`);
    } catch (error) {
      logger.error('Error creating cohort:', error);
      toast.error('Failed to create cohort');
    } finally {
      setCreating(false);
    }
  };

  const filteredCohorts = cohorts.filter(cohort =>
    cohort.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cohort.description && cohort.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getCohortTypeColor = (type) => {
    switch (type) {
      case 'ongoing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'time_bound': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'project_based': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onCohortChange(null);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={`w-full justify-between ${selectedCohort ? 'text-slate-900' : 'text-slate-500'} ${className}`}
            data-testid="cohort-selector-trigger"
          >
            <div className="flex items-center gap-2 truncate">
              <Users className="h-4 w-4 shrink-0 text-slate-400" />
              {selectedCohort ? (
                <span className="truncate">{selectedCohort.name}</span>
              ) : (
                <span>{placeholder}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {selectedCohort && (
                <X 
                  className="h-4 w-4 text-slate-400 hover:text-slate-600" 
                  onClick={handleClearSelection}
                />
              )}
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[350px] p-0" align="start">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search cohorts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="cohort-search-input"
              />
            </div>
          </div>
          
          <div className="max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : filteredCohorts.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">
                {searchQuery ? 'No cohorts match your search' : 'No cohorts found'}
              </div>
            ) : (
              <div className="p-2">
                {filteredCohorts.map((cohort) => (
                  <button
                    key={cohort.id}
                    onClick={() => {
                      onCohortChange(cohort);
                      setOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg mb-1 transition-colors ${
                      selectedCohort?.id === cohort.id 
                        ? 'bg-teal-50 border border-teal-200' 
                        : 'hover:bg-slate-50 border border-transparent'
                    }`}
                    data-testid={`cohort-option-${cohort.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate">
                            {cohort.name}
                          </span>
                          {selectedCohort?.id === cohort.id && (
                            <Check className="h-4 w-4 text-teal-600 shrink-0" />
                          )}
                        </div>
                        {cohort.description && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {cohort.description}
                          </p>
                        )}
                      </div>
                      <Badge variant="outline" className={`text-xs shrink-0 ${getCohortTypeColor(cohort.type)}`}>
                        {cohort.type?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      <span>{cohort.member_count || 0} members</span>
                      <span>{cohort.program_count || 0} programs</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {showCreateOption && (
            <div className="p-2 border-t bg-slate-50">
              <Button
                variant="ghost"
                className="w-full justify-start text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                onClick={() => {
                  setShowCreateDialog(true);
                  setOpen(false);
                }}
                data-testid="create-new-cohort-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Cohort
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Create Cohort Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-600" />
              Create New Cohort
            </DialogTitle>
            <DialogDescription>
              Define a target group for your learning intervention analysis
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cohort-name">Cohort Name *</Label>
              <Input
                id="cohort-name"
                placeholder="e.g., Sales Team Q2 2026"
                value={newCohort.name}
                onChange={(e) => setNewCohort(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-cohort-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cohort-type">Cohort Type</Label>
              <Select
                value={newCohort.type}
                onValueChange={(value) => setNewCohort(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger data-testid="new-cohort-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COHORT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <div>
                        <span className="font-medium">{type.label}</span>
                        <span className="text-xs text-slate-500 ml-2">{type.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cohort-description">Description</Label>
              <Textarea
                id="cohort-description"
                placeholder="Brief description of this cohort..."
                value={newCohort.description}
                onChange={(e) => setNewCohort(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                data-testid="new-cohort-description-input"
              />
            </div>
            
            {newCohort.type === 'time_bound' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={newCohort.start_date}
                    onChange={(e) => setNewCohort(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={newCohort.end_date}
                    onChange={(e) => setNewCohort(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
            )}
            
            {/* Demographics Section - Collapsible */}
            <div className="border rounded-lg">
              <button
                type="button"
                onClick={() => setShowDemographics(!showDemographics)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-teal-600" />
                  <span className="font-medium text-sm">Demographics (Optional)</span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showDemographics ? 'rotate-180' : ''}`} />
              </button>
              
              {showDemographics && (
                <div className="p-3 pt-0 space-y-3 border-t">
                  <p className="text-xs text-slate-500">
                    Add demographics to include a detailed Cohort Summary in exported Business Cases.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Population Size</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 250"
                        value={newCohort.demographics.population_size}
                        onChange={(e) => setNewCohort(prev => ({ 
                          ...prev, 
                          demographics: { ...prev.demographics, population_size: e.target.value }
                        }))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Total Workforce</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 5000"
                        value={newCohort.demographics.total_workforce}
                        onChange={(e) => setNewCohort(prev => ({ 
                          ...prev, 
                          demographics: { ...prev.demographics, total_workforce: e.target.value }
                        }))}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Departments</Label>
                    <Input
                      placeholder="Sales: 100, Marketing: 80, Engineering: 70"
                      value={newCohort.demographics.departments}
                      onChange={(e) => setNewCohort(prev => ({ 
                        ...prev, 
                        demographics: { ...prev.demographics, departments: e.target.value }
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Regions</Label>
                    <Input
                      placeholder="North America: 150, EMEA: 60, APAC: 40"
                      value={newCohort.demographics.regions}
                      onChange={(e) => setNewCohort(prev => ({ 
                        ...prev, 
                        demographics: { ...prev.demographics, regions: e.target.value }
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Tenure Levels</Label>
                    <Input
                      placeholder="0-2 years: 80, 3-5 years: 100, 5+ years: 70"
                      value={newCohort.demographics.tenure_breakdown}
                      onChange={(e) => setNewCohort(prev => ({ 
                        ...prev, 
                        demographics: { ...prev.demographics, tenure_breakdown: e.target.value }
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Role Levels</Label>
                    <Input
                      placeholder="IC: 175, Manager: 50, Director+: 25"
                      value={newCohort.demographics.role_levels}
                      onChange={(e) => setNewCohort(prev => ({ 
                        ...prev, 
                        demographics: { ...prev.demographics, role_levels: e.target.value }
                      }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createCohort} 
              disabled={creating || !newCohort.name.trim()}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="create-cohort-submit-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Cohort
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CohortSelector;
